import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout, Pagination, Avatar } from "../../components"

/* Widgets */
import { Filter, Detail } from "../../widgets/Transaction"

/* REST API */
import { list } from "../../api/Transaction"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Icons */
import { SearchIcon, CloseIcon, FilterIcon, EyeIcon, CopyIcon, WalletIcon, PlayerIcon, VipIcon, AlertIcon, RefundIcon, DebitIcon, CreditIcon, NetIcon, StudioIcon, OperatorIcon, TableIcon, LayersIcon } from "../../widgets/Transaction/TransactionIcons"

/* Utils */
import { getState, getStateText, getGame, getAvatar, getCurrency, isEmpty, formatCount, formatDay, formatTime, formatAgo } from "../../widgets/Transaction/TransactionUtils"

/* Translate */
import { t } from "../../translates/translate"

/* Sweet alert */
import Swal from "../../helpers/alert"

/* Moment */
import moment from "moment"


/* PAGE SIZE — THE SAME NUMBER THE CONTROLLER USES */
const SIZE = 20

/* DELAY BEFORE THE SEARCH REQUEST LEAVES */
const DELAY = 400

/* EMPTY ANSWER — THE SAME SHAPE THE CONTROLLER SENDS */
const EMPTY = {
    totals: { debit: 0, credit: 0, debitCount: 0, creditCount: 0 },
    stats: { players: 0, vips: 0, failed: 0, pending: 0, refunds: 0 },
}


/* Page Transactions */
class Transactions extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            status: "loading",
            count: 0,
            search: "",
            operators: [],
            tables: [],
            selected: "all",
            start: null,
            end: null,
            txStatus: "",
            type: "",
            refund: "",
            testers: false,
            vips: true,
            totals: EMPTY.totals,
            stats: EMPTY.stats,
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page — a new page starts from the top with the loader */
        if (prevProps.params.page !== page) {
            window.scrollTo(0, 0)
            this.load(true)
        }

    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    /* Get Page */
    getPage = () => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page */
        if (Number.isInteger(parseInt(page))) {
            return parseInt(page)
        }

        return 1
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { search, operators, tables, start, end, txStatus, type, refund, testers, vips } = this.state
        const page = this.getPage()

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list({
            page,
            search,
            operators: operators.map((item) => item.id).join(","),
            tables: tables.map((item) => item.id).join(","),
            start: start ? moment(start).format("YYYY-MM-DD HH:mm:ss") : "",
            end: end ? moment(end).format("YYYY-MM-DD HH:mm:ss") : "",
            status: txStatus,
            type,
            refund,
            testers,
            vips,
        }).then((response) => {
            if (response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    totals: response.data.totals ? response.data.totals : EMPTY.totals,
                    stats: response.data.stats ? response.data.stats : EMPTY.stats,
                })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0, ...EMPTY })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0, ...EMPTY })
            }
            else {
                this.setState({ status: "error", data: [], count: 0, ...EMPTY })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0, ...EMPTY })
        })

    }

    /* Change Search — the request leaves only after the typing stops */
    changeSearch = (search) => {

        this.setState({ search })

        clearTimeout(this._timer)
        this._timer = setTimeout(() => this.apply(), DELAY)
    }

    /* Apply Filter */
    applyFilter = ({ selected, start, end, operators, tables, status, type, refund, testers, vips }) => {
        this.setState({ selected, start, end, operators, tables, txStatus: status, type, refund, testers, vips }, () => this.apply())
    }

    /* Apply Action — every filter restarts from the first page */
    apply = () => {

        /* Fields */
        const { navigate } = this.props

        /* Check Page */
        if (this.getPage() !== 1) {
            navigate("/transaction/1")
            return
        }

        this.load()
    }

    /* Copy Action — the number of a transaction goes to the clipboard straight from the row */
    copy = (value) => {

        /* Check Value */
        if (!value) {
            Swal.fire(utils.notification(t("gtx.copyFailed"), "error"))
            return
        }

        /* Check Clipboard — an insecure origin has none */
        if (!navigator.clipboard) {
            Swal.fire(utils.notification(t("gtx.copyFailed"), "error"))
            return
        }

        navigator.clipboard.writeText(String(value)).then(() => {
            Swal.fire(utils.notification(t("gtx.copied"), "success"))
        }).catch(() => {
            Swal.fire(utils.notification(t("gtx.copyFailed"), "error"))
        })

    }

    /* Count Of The Active Filters — the badge of the filter button */
    getCount = () => {

        /* Fields */
        const { selected, operators, tables, txStatus, type, refund, testers, vips } = this.state
        let count = 0

        if (selected !== "all") {
            count = count + 1
        }

        if (operators.length > 0) {
            count = count + 1
        }

        if (tables.length > 0) {
            count = count + 1
        }

        if (txStatus !== "") {
            count = count + 1
        }

        if (type !== "") {
            count = count + 1
        }

        if (refund !== "") {
            count = count + 1
        }

        if (!testers) {
            count = count + 1
        }

        if (!vips) {
            count = count + 1
        }

        /* Send Result */
        return count
    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="gtx--search">

                <SearchIcon />

                <input
                    value={search}
                    onChange={(event) => this.changeSearch(event.target.value)}
                    placeholder={t("gtx.search")}
                />

                {search !== "" &&
                    <div onClick={() => this.changeSearch("")} className="gtx--search-clear">
                        <CloseIcon size={12} />
                    </div>
                }

            </div>
        )
    }

    /* Draw Filter Button */
    _filter = () => {

        /* Fields */
        const { selected, start, end, operators, tables, txStatus, type, refund, testers, vips } = this.state
        const count = this.getCount()

        return (
            <div
                onClick={() => this._filterModal.open({ selected, start, end, operators, tables, status: txStatus, type, refund, testers, vips })}
                className={`gtx--filter ${count > 0 ? "active" : ""}`}
            >
                <FilterIcon size={15} />
                <span>{t("gtx.filter")}</span>
                {count > 0 && <div className="gtx--filter-count">{count}</div>}
            </div>
        )
    }

    /* Draw One Number Of The Filter */
    _stat = (icon, title, value, tone = "") => {

        return (
            <div className={`gtx--stat ${tone}`}>

                <div className="gtx--stat-icon">{icon}</div>

                <div className="gtx--stat-text">
                    <div className="gtx--stat-title">{title}</div>
                    <div className="gtx--stat-value">{value}</div>
                </div>

            </div>
        )
    }

    /* Draw Numbers — what the current filter really holds */
    _numbers = () => {

        /* Fields */
        const { count, stats } = this.state

        return (
            <div className="gtx--stats">

                {this._stat(<LayersIcon />, t("gtx.total"), formatCount(count), "blue")}
                {this._stat(<PlayerIcon />, t("gtx.playersCount"), formatCount(stats.players), "violet")}
                {this._stat(<VipIcon />, t("gtx.vipsCount"), formatCount(stats.vips), "gold")}
                {this._stat(<AlertIcon />, t("gtx.failedCount"), formatCount(stats.failed), "red")}
                {this._stat(<RefundIcon />, t("gtx.refundsCount"), formatCount(stats.refunds), "orange")}

            </div>
        )
    }

    /* Draw Money — the sums of the whole filter, in USD */
    _summary = () => {

        /* Fields */
        const { totals } = this.state
        const debit = Number(totals.debit) || 0
        const credit = Number(totals.credit) || 0
        const net = debit - credit

        return (
            <div className="gtx--summary">

                {/* Debit */}
                <div className="gtx--card debit">
                    <div className="gtx--card-head">
                        <DebitIcon />
                        {t("gtx.totalDebit")}
                    </div>
                    <div className="gtx--card-value">{utils.convertor(debit, "USD", 1)}</div>
                    <div className="gtx--card-note">{formatCount(totals.debitCount)} {t("gtx.summaryCount")}</div>
                </div>

                {/* Credit */}
                <div className="gtx--card credit">
                    <div className="gtx--card-head">
                        <CreditIcon />
                        {t("gtx.totalCredit")}
                    </div>
                    <div className="gtx--card-value">{utils.convertor(credit, "USD", 1)}</div>
                    <div className="gtx--card-note">{formatCount(totals.creditCount)} {t("gtx.summaryCount")}</div>
                </div>

                {/* Net */}
                <div className={`gtx--card ${net >= 0 ? "positive" : "negative"}`}>
                    <div className="gtx--card-head">
                        <NetIcon />
                        {t("gtx.totalNet")}
                    </div>
                    <div className="gtx--card-value">{utils.convertor(net, "USD", 1)}</div>
                    <div className="gtx--card-note">{net >= 0 ? t("gtx.netPositive") : t("gtx.netNegative")}</div>
                </div>

            </div>
        )
    }

    /* Draw Balance — the wallet of the player before and after, empty means the operator sent nothing */
    _balance = (item) => {

        /* Fields */
        const currency = getCurrency(item.currency)

        /* Check Balance */
        if (isEmpty(item.balanceBefore) && isEmpty(item.balanceAfter)) {
            return <div className="gtx--balance empty">—</div>
        }

        return (
            <div className="gtx--balance">
                <span>{isEmpty(item.balanceBefore) ? "—" : utils.convertor(item.balanceBefore, currency, 1)}</span>
                <span className="gtx--balance-arrow">→</span>
                <span className="gtx--balance-after">{isEmpty(item.balanceAfter) ? "—" : utils.convertor(item.balanceAfter, currency, 1)}</span>
            </div>
        )
    }

    /* Draw Actions */
    _actions = (item) => {

        return (
            <>
                <div onClick={() => this._detailModal.open(item.id)} className="gtx--action" title={t("gtx.detailTitle")}>
                    <EyeIcon />
                </div>

                <div onClick={() => this.copy(item.number)} className="gtx--action" title={t("gtx.copyNumber")}>
                    <CopyIcon size={15} />
                </div>
            </>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, search } = this.state
        const { navigate } = this.props
        const page = this.getPage()

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="gtx--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="gtx--page">

                {/* Head */}
                <div className="gtx--table-header">
                    <div className="gtx--column gtx--col-number">#</div>
                    <div className="gtx--column gtx--col-game">{t("gtx.game")}</div>
                    <div className="gtx--column gtx--col-player">{t("gtx.player")}</div>
                    <div className="gtx--column gtx--col-reason">{t("gtx.reason")}</div>
                    <div className="gtx--column gtx--col-amount">{t("gtx.amount")}</div>
                    <div className="gtx--column gtx--col-state">{t("gtx.status")}</div>
                    <div className="gtx--column gtx--col-date">{t("gtx.date")}</div>
                    <div className="gtx--column gtx--col-actions" />
                </div>

                {/* Empty */}
                {data.length === 0 &&
                    <div className="gtx--empty">
                        <WalletIcon size={40} />
                        <span>{search !== "" || this.getCount() > 0 ? t("gtx.nothingFound") : t("gtx.empty")}</span>
                    </div>
                }

                {/* Body */}
                {data.map((item, index) =>
                    <div onClick={() => this._detailModal.open(item.id)} className="gtx--row" key={`${index}`}>

                        {/* Number */}
                        <div className="gtx--column gtx--col-number">{(page - 1) * SIZE + index + 1}</div>

                        {/* Game */}
                        <div className="gtx--column gtx--col-game">

                            <div className={`gtx--icon ${item.type}`}>
                                {String(item.type) === "credit" ? <CreditIcon size={18} /> : <DebitIcon size={18} />}
                            </div>

                            <div className="gtx--game-text">
                                <div className="gtx--game-name">{getGame(item)}</div>
                                <div className="gtx--game-note">
                                    <StudioIcon size={11} />
                                    {utils.checkValue(item.studio, "—")}
                                </div>
                            </div>

                        </div>

                        {/* Player */}
                        <div className="gtx--column gtx--col-player">

                            <Avatar uri={getAvatar(item)} size={38} />

                            <div className="gtx--player-text">

                                <div className="gtx--player-name">
                                    <span className="gtx--player-value">{utils.checkValue(item.firstName, "—")}</span>
                                    {parseInt(item.isTester) === 1 && <span className="gtx--tag tester">{t("gtx.tester")}</span>}
                                    {parseInt(item.isVip) === 1 && <span className="gtx--tag vip">VIP</span>}
                                </div>

                                <div className="gtx--player-code">{item.player}</div>

                                <div className="gtx--operator" title={utils.checkValue(item.operator, "")}>
                                    <OperatorIcon size={11} />
                                    <span>{utils.checkValue(item.operator, "—")}</span>
                                </div>

                            </div>

                        </div>

                        {/* Reason */}
                        <div className="gtx--column gtx--col-reason">
                            <div className="gtx--reason-name">
                                {utils.checkValue(item.reason, "—")}
                                {parseInt(item.box) > 0 ? <span className="gtx--box">BOX {item.box}</span> : null}
                            </div>
                            <div className="gtx--reason-note">
                                <TableIcon size={11} />
                                {utils.checkValue(item.number, "—")}
                            </div>
                        </div>

                        {/* Amount */}
                        <div className="gtx--column gtx--col-amount">
                            <div className={`gtx--total ${item.type}`}>{utils.convertor(item.total, getCurrency(item.currency), 1)}</div>
                            <div className="gtx--usd">{utils.convertor(item.totalUSD, "USD", 1)}</div>
                            {this._balance(item)}
                        </div>

                        {/* Status */}
                        <div className="gtx--column gtx--col-state">
                            <div className={`gtx--state ${getState(item.status)}`}>{getStateText(item.status)}</div>
                            {parseInt(item.isRefund) === 1 && <div className="gtx--refund">{t("gtx.isRefund")}</div>}
                        </div>

                        {/* Date */}
                        <div className="gtx--column gtx--col-date">
                            <div className="gtx--date">{formatDay(item.createdAt)}</div>
                            <div className="gtx--time">{formatTime(item.createdAt)}</div>
                            <div className="gtx--ago">{formatAgo(item.createdAt)}</div>
                        </div>

                        {/* Actions */}
                        <div onClick={(event) => event.stopPropagation()} className="gtx--column gtx--col-actions">{this._actions(item)}</div>

                    </div>
                )}

                {/* Pagination */}
                <Pagination size={SIZE} page={page} total={count} onPageChange={(page) => navigate(`/transaction/${page}`)} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status } = this.state

        return (
            <div className="page">

                <div className="page-header gtx--header">

                    <h1>{t("gtx.title")}</h1>

                    {status === "ok" &&
                        <div className="gtx--tools">

                            {/* Draw Search */}
                            {this._search()}

                            {/* Draw Filter Button */}
                            {this._filter()}

                        </div>
                    }

                </div>

                {/* Draw Numbers */}
                {status === "ok" && this._numbers()}

                {/* Draw Money */}
                {status === "ok" && this._summary()}

                {/* Draw Content */}
                {this._content()}

                {/* Draw Detail Modal */}
                <Detail ref={(ref) => this._detailModal = ref} />

                {/* Draw Filter Modal */}
                <Filter apply={this.applyFilter} ref={(ref) => this._filterModal = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(Transactions))
